import { Eye, EyeOff, Loader2 } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"

import { AuthLayout } from "./auth-layout"
import {
  type FieldErrors,
  hasFieldError,
  type LoginError,
  validateLoginInput,
} from "./auth-data"
import { useCurrentUser, useLoginMutation } from "./auth-store"

const NO_ERRORS: FieldErrors = { email: null, password: null }

/**
 * `?redirect=` is user-controlled — anything other than a same-origin
 * path lets an attacker turn our post-login navigate into an open
 * redirect (phishing). Rejected forms: schemes (`https:`), authority
 * shortcuts (`//host`, `/\\host`), and any control characters that
 * could smuggle through downstream routers.
 */
function safeRedirect(target: string | null): string {
  if (!target) return "/reviews"
  if (target.length > 512) return "/reviews"
  if (/[\r\n\t\0]/.test(target)) return "/reviews"
  if (!target.startsWith("/")) return "/reviews"
  if (target.startsWith("//") || target.startsWith("/\\")) return "/reviews"
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(target)) return "/reviews"
  return target
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = safeRedirect(searchParams.get("redirect"))

  const login = useLoginMutation()
  const currentUser = useCurrentUser()
  const currentUserId = currentUser?.id ?? null

  // A reviewer who already has a session should not see the form.
  React.useEffect(() => {
    if (currentUserId) navigate(redirectTo, { replace: true })
  }, [currentUserId, navigate, redirectTo])

  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [rememberMe, setRememberMe] = React.useState(false)
  const [showPassword, setShowPassword] = React.useState(false)
  const [capsOn, setCapsOn] = React.useState(false)
  const [touched, setTouched] = React.useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  })
  const [attempted, setAttempted] = React.useState(false)

  const fieldErrors = React.useMemo<FieldErrors>(() => {
    if (!attempted && !touched.email && !touched.password) return NO_ERRORS
    const errors = validateLoginInput({ email, password })
    return {
      email: attempted || touched.email ? errors.email : null,
      password: attempted || touched.password ? errors.password : null,
    }
  }, [email, password, touched, attempted])

  const topError = useTopLevelError(login.error)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setAttempted(true)
    const errors = validateLoginInput({ email, password })
    if (hasFieldError(errors)) return
    if (login.isPending) return
    try {
      await login.mutateAsync({ email: email.trim(), password, rememberMe })
      navigate(redirectTo, { replace: true })
    } catch {
      /* surfaced via login.error */
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Review printed labels against their approved masters, and share the findings with your client."
      footer={
        <p>
          No account yet?{" "}
          <span className="text-text-2">
            Your Pyxis contact can set one up — access is granted per engagement.
          </span>
        </p>
      }
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={login.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {login.isPending ? "Signing in…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
          </Alert>
        )}

        <FormField label="Email" required error={fieldErrors.email}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@company.com"
            inputSize="lg"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value)
              if (login.error) login.reset()
            }}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            disabled={login.isPending}
            autoFocus
          />
        </FormField>

        <FormField label="Password" required error={fieldErrors.password}>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              inputSize="lg"
              value={password}
              onChange={(e) => {
                setPassword(e.currentTarget.value)
                if (login.error) login.reset()
              }}
              onBlur={() => {
                setTouched((t) => ({ ...t, password: true }))
                setCapsOn(false)
              }}
              onKeyDown={(e) => {
                const on = e.getModifierState?.("CapsLock")
                if (typeof on === "boolean") setCapsOn(on)
              }}
              onKeyUp={(e) => {
                const on = e.getModifierState?.("CapsLock")
                if (typeof on === "boolean") setCapsOn(on)
              }}
              disabled={login.isPending}
              wrapperClassName="pr-9"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              tabIndex={-1}
              disabled={login.isPending}
              className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-sm text-text-3 transition-colors duration-120 ease-out hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)] disabled:opacity-50"
            >
              {showPassword ? (
                <EyeOff className="size-4" aria-hidden="true" />
              ) : (
                <Eye className="size-4" aria-hidden="true" />
              )}
            </button>
          </div>
          {capsOn && (
            <p className="mt-1 text-caption text-print" role="status">
              Caps Lock is on.
            </p>
          )}
        </FormField>

        <div className="flex items-center justify-between gap-3">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 text-body-sm text-text-2">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.currentTarget.checked)}
              disabled={login.isPending}
              className="size-4 cursor-pointer rounded-sm border-border-strong text-brand accent-[color:var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)] disabled:cursor-not-allowed disabled:opacity-50"
            />
            Remember me
          </label>
          <Link
            to="/forgot-password"
            className="text-body-sm text-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={login.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={login.isPending}
        >
          {login.isPending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
}

/**
 * Turn a LoginError into a banner. Countdown ticks per second while a
 * rate-limit error carries a `retryAfterSeconds`. Returns null when
 * there is no error to show.
 */
function useTopLevelError(error: LoginError | null): TopError | null {
  const [remaining, setRemaining] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!error || error.retryAfterSeconds == null) {
      setRemaining(null)
      return
    }
    setRemaining(error.retryAfterSeconds)
    const t = setInterval(() => {
      setRemaining((prev) => {
        if (prev == null) return null
        if (prev <= 1) {
          clearInterval(t)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [error])

  if (!error) return null
  switch (error.kind) {
    case "invalid_credentials":
      return { title: "Sign in failed", body: "Email or password is incorrect." }
    case "account_locked":
      return {
        title: "Account locked",
        body: "This account is locked. Contact your Pyxis admin to unlock it.",
      }
    case "rate_limited":
      return {
        title: "Too many attempts",
        body:
          remaining && remaining > 0
            ? `Try again in ${remaining}s.`
            : "You can try again now.",
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
      }
    default:
      return { title: "Sign in failed", body: error.message }
  }
}
